import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from 'firebase/firestore';
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import AIService from './AIService';
import { db, storage } from '../firebase/config';
import type { App } from '../types/app';
import { buildAppContextLines } from '../utils/appContextPrompt';
import type {
  AppViolationReviewReport,
  ScreenshotReviewInput,
  ViolationIssue,
  ViolationPlatform,
  ViolationPlatformGroup,
} from '../types/appViolation';

const REPORTS_COLLECTION = 'appStoreViolationReports';

const REVIEW_MODELS = {
  default: 'gemini-2.5-flash-lite',
};

type AIReviewOutput = {
  summary?: string;
  groupedFindings?: Array<{
    platform?: string;
    overview?: string;
    issues?: Array<{
      title?: string;
      severity?: string;
      guidelineReference?: string;
      whyItCanBeRejected?: string;
      screenshotRefs?: string[];
      fixSteps?: string[];
    }>;
  }>;
  globalFixRecommendations?: string[];
};

type AIRawIssue = {
  title?: string;
  severity?: string;
  guidelineReference?: string;
  whyItCanBeRejected?: string;
  screenshotRefs?: string[];
  fixSteps?: string[];
};

class AppViolationService {
  private generateReportId(): string {
    return `app_violation_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  private sanitizeFileName(fileName: string): string {
    return fileName
      .trim()
      .replace(/[^\w.\-()]+/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 120);
  }

  private toDate(value: any): Date {
    if (value instanceof Timestamp) return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
  }

  private async preprocessScreenshot(file: File): Promise<{ dataUrl: string; mimeType: string }> {
    const bitmap = await createImageBitmap(file);
    const maxSide = 1280;
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to process uploaded screenshot.');
    }

    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const preferredMime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const dataUrl = canvas.toDataURL(preferredMime, preferredMime === 'image/jpeg' ? 0.82 : undefined);
    return { dataUrl, mimeType: preferredMime };
  }

  private toStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0);
  }

  private normalizeIssue(raw: AIRawIssue | undefined, issueIndex: number): ViolationIssue {
    const severityRaw = typeof raw?.severity === 'string' ? raw.severity.toLowerCase() : 'medium';
    const severity = ['low', 'medium', 'high', 'critical'].includes(severityRaw)
      ? (severityRaw as ViolationIssue['severity'])
      : 'medium';

    return {
      id: `issue_${issueIndex + 1}_${Math.random().toString(36).slice(2, 7)}`,
      title: String(raw?.title || 'Potential Policy Risk').trim(),
      severity,
      guidelineReference: String(raw?.guidelineReference || 'Guideline reference not specified').trim(),
      whyItCanBeRejected: String(raw?.whyItCanBeRejected || 'Potential guideline non-compliance').trim(),
      screenshotRefs: this.toStringArray(raw?.screenshotRefs),
      fixSteps: this.toStringArray(raw?.fixSteps),
    };
  }

  private normalizeFindings(raw: AIReviewOutput['groupedFindings']): ViolationPlatformGroup[] {
    if (!Array.isArray(raw)) return [];

    return raw
      .map((group) => {
        const platform = (typeof group.platform === 'string' ? group.platform.toLowerCase() : '') as ViolationPlatform;
        if (platform !== 'ios' && platform !== 'android') return null;

        const normalizedIssues = (Array.isArray(group.issues) ? group.issues : [])
          .map((issue, issueIndex) => this.normalizeIssue(issue, issueIndex))
          .filter((issue) => issue.title.length > 0);

        return {
          platform,
          overview: String(group.overview || '').trim(),
          totalIssues: normalizedIssues.length,
          issues: normalizedIssues,
        } as ViolationPlatformGroup;
      })
      .filter((group): group is ViolationPlatformGroup => Boolean(group));
  }

  private buildPrompt(app: App, screenshots: ScreenshotReviewInput[]): string {
    const screenshotLabels = screenshots
      .map((screenshot, index) => `${index + 1}. ${screenshot.fileName} (${screenshot.mimeType})`)
      .join('\n');

    return [
      'You are a senior mobile app compliance reviewer for Apple App Store and Google Play Store.',
      'Analyze the provided app screenshots for potential policy/guideline violations that can lead to rejection.',
      'Only report violations that are likely and actionable. Avoid vague statements.',
      '',
      'App context:',
      ...buildAppContextLines(app).map((line) => `- ${line}`),
      '',
      'Uploaded screenshots:',
      screenshotLabels,
      '',
      'Requirements:',
      '1. Group findings by platform (`ios` and/or `android`).',
      '2. For each issue provide: title, severity, guidelineReference, whyItCanBeRejected, screenshotRefs, and specific fixSteps.',
      '3. screenshotRefs must reference screenshot file names from the uploaded list.',
      '4. If no issues are found for a platform, you may omit that platform.',
      '5. Keep fix steps practical and implementation-ready.',
      '6. Return ONLY valid JSON with this exact top-level shape:',
      '{ "summary": string, "groupedFindings": [{ "platform": "ios|android", "overview": string, "issues": [{ "title": string, "severity": "low|medium|high|critical", "guidelineReference": string, "whyItCanBeRejected": string, "screenshotRefs": string[], "fixSteps": string[] }] }], "globalFixRecommendations": string[] }',
    ].join('\n');
  }

  private parseAIReviewOutput(rawText: string): AIReviewOutput {
    const text = String(rawText || '').trim();
    if (!text) return {};

    const tryParse = (candidate: string): AIReviewOutput | null => {
      try {
        return JSON.parse(candidate) as AIReviewOutput;
      } catch {
        return null;
      }
    };

    const direct = tryParse(text);
    if (direct) return direct;

    const fencedMatch = text.match(/```json\s*([\s\S]*?)\s*```/i) || text.match(/```\s*([\s\S]*?)\s*```/i);
    if (fencedMatch?.[1]) {
      const fenced = tryParse(fencedMatch[1].trim());
      if (fenced) return fenced;
    }

    const firstBrace = text.indexOf('{');
    const lastBrace = text.lastIndexOf('}');
    if (firstBrace >= 0 && lastBrace > firstBrace) {
      const sliced = tryParse(text.slice(firstBrace, lastBrace + 1));
      if (sliced) return sliced;
    }

    return {};
  }

  private stripUndefined(value: any): any {
    if (Array.isArray(value)) {
      return value.map((item) => this.stripUndefined(item));
    }
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([, entry]) => entry !== undefined)
          .map(([key, entry]) => [key, this.stripUndefined(entry)])
      );
    }
    return value;
  }

  private async uploadScreenshots(params: {
    userId: string;
    appId: string;
    reportId: string;
    screenshots: File[];
  }): Promise<ScreenshotReviewInput[]> {
    const { userId, appId, reportId, screenshots } = params;
    const uploaded = await Promise.all(
      screenshots.map(async (file, index) => {
        const safeName = this.sanitizeFileName(file.name || `screenshot_${index + 1}.png`);
        const path = `apps/${appId}/violation-reviews/${reportId}/${String(index + 1).padStart(2, '0')}_${safeName}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, file, {
          contentType: file.type || 'image/png',
          customMetadata: {
            userId,
            appId,
            reportId,
            originalFileName: file.name || safeName,
          },
        });
        const downloadUrl = await getDownloadURL(fileRef);
        return {
          fileName: file.name || safeName,
          mimeType: file.type || 'image/png',
          storagePath: path,
          downloadUrl,
        } as ScreenshotReviewInput;
      })
    );
    return uploaded;
  }

  async reviewScreenshots(params: {
    userId: string;
    app: App;
    screenshots: File[];
    model?: string;
  }): Promise<AppViolationReviewReport> {
    const { userId, app, screenshots, model } = params;
    if (!userId) throw new Error('User is required.');
    if (!app?.appId) throw new Error('A registered app is required.');
    if (!Array.isArray(screenshots) || screenshots.length === 0) {
      throw new Error('Upload at least one screenshot.');
    }

    const safeScreenshots = screenshots.slice(0, 12);
    const start = Date.now();
    const reportId = this.generateReportId();
    const processedImages = await Promise.all(
      safeScreenshots.map((file) => this.preprocessScreenshot(file))
    );
    const screenshotInputs = await this.uploadScreenshots({
      userId,
      appId: app.appId,
      reportId,
      screenshots: safeScreenshots,
    });

    const aiResponse = await AIService.generateText(
      this.buildPrompt(app, screenshotInputs),
      {
        model: model || undefined,
        temperature: 0.2,
        maxTokens: 4096,
        inputImages: processedImages,
      }
    );
    const aiOutput = this.parseAIReviewOutput(aiResponse.text);

    const groupedFindings = this.normalizeFindings(aiOutput.groupedFindings);
    const report: AppViolationReviewReport = {
      reportId,
      userId,
      appId: app.appId,
      appName: app.name,
      modelUsed: model || REVIEW_MODELS.default,
      summary: String(aiOutput.summary || 'Screenshot compliance review completed.').trim(),
      screenshots: screenshotInputs,
      groupedFindings,
      globalFixRecommendations: this.toStringArray(aiOutput.globalFixRecommendations),
      reviewMetadata: {
        generatedAt: new Date(),
        durationSeconds: Number(((Date.now() - start) / 1000).toFixed(2)),
        screenshotCount: screenshotInputs.length,
      },
    };

    return report;
  }

  async saveReport(report: AppViolationReviewReport): Promise<void> {
    const payload = this.stripUndefined({
      ...report,
      reviewMetadata: {
        ...report.reviewMetadata,
        generatedAt: report.reviewMetadata.generatedAt || new Date(),
      },
      savedAt: serverTimestamp(),
    });
    await addDoc(collection(db, REPORTS_COLLECTION), payload);
  }

  async getReportsByApp(userId: string, appId: string): Promise<AppViolationReviewReport[]> {
    const reportsRef = collection(db, REPORTS_COLLECTION);
    const reportsQuery = query(
      reportsRef,
      where('userId', '==', userId),
      where('appId', '==', appId)
    );
    const snapshot = await getDocs(reportsQuery);

    return snapshot.docs
      .map((docItem) => {
        const data = docItem.data() as any;
        return {
          reportId: data.reportId,
          userId: data.userId,
          appId: data.appId,
          appName: data.appName,
          modelUsed: data.modelUsed || REVIEW_MODELS.default,
          summary: data.summary || '',
          screenshots: Array.isArray(data.screenshots) ? data.screenshots : [],
          groupedFindings: this.normalizeFindings(data.groupedFindings),
          globalFixRecommendations: this.toStringArray(data.globalFixRecommendations),
          reviewMetadata: {
            generatedAt: this.toDate(data?.reviewMetadata?.generatedAt || data.createdAt || new Date()),
            durationSeconds: Number(data?.reviewMetadata?.durationSeconds || 0),
            screenshotCount: Number(data?.reviewMetadata?.screenshotCount || 0),
          },
          savedAt: data.savedAt ? this.toDate(data.savedAt) : undefined,
        } as AppViolationReviewReport;
      })
      .sort(
        (a, b) =>
          (b.savedAt || b.reviewMetadata.generatedAt).getTime() -
          (a.savedAt || a.reviewMetadata.generatedAt).getTime()
      );
  }

  async deleteReport(userId: string, reportId: string): Promise<void> {
    const reportsRef = collection(db, REPORTS_COLLECTION);
    const reportsQuery = query(
      reportsRef,
      where('userId', '==', userId),
      where('reportId', '==', reportId)
    );
    const snapshot = await getDocs(reportsQuery);
    await Promise.all(
      snapshot.docs.map(async (docItem) => {
        const data = docItem.data() as any;
        const screenshotPaths = Array.isArray(data?.screenshots)
          ? (data.screenshots as ScreenshotReviewInput[])
              .map((item) => item?.storagePath)
              .filter((item): item is string => Boolean(item))
          : [];

        await Promise.all(
          screenshotPaths.map(async (path) => {
            try {
              await deleteObject(storageRef(storage, path));
            } catch (error: any) {
              if (error?.code !== 'storage/object-not-found') {
                throw error;
              }
            }
          })
        );

        await deleteDoc(docItem.ref);
      })
    );
  }
}

export default new AppViolationService();
